import {
  AfterContentInit,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChange,
  ViewEncapsulation
} from '@angular/core';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {TreeTable} from 'primeng/treetable';
import {LoaderService} from '../../services/loader.service';
import {of, Subject} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {SiTreeNode, TreeTableNodeEvent} from '../../models/tree-table.model';
import {DispatcherService} from '../../services/dispatcher.service';
import {TranslateService} from '@ngx-translate/core';

const treeTableDefaultConfig = {
  paginator: true,
  rows: 20,
  rowsPerPageOptions: [5, 10, 20, 50, 75, 100],
  resizableColumns: true,
  lazy: true
};

@Component({
  selector: 'si-tree-table',
  templateUrl: './tree-table.component.html',
  styleUrls: ['./tree-table.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TreeTableComponent implements OnInit, AfterContentInit, OnDestroy {
  @Input() id: string;
  @Input() path: string;
  @Input() filters: string;
  @Input() valueAttr = 'id';
  @Input() valueAttrParent = 'id';
  @Input() valueType: string;
  @Input() disabledRowFunction: Function;
  @Output() modelChange = new EventEmitter<any[]>();
  @ContentChild(TreeTable, {static: true}) pTreeTable: TreeTable;
  lastLazyLoadData;
  reloadTreeTableSubscription: number;

  // Indicates if there is user activity or not.
  activeDom = true;

  httpRequest$ = new Subject();
  lastHttpParams: HttpParams;
  lastFirst: number;
  lastRows: number;
  isLoading = false;
  rowsText: string;
  ofText: string;
  i = 0;
  itemAuxId: number;
  nodeToBeDeletedFound = false;
  private paginationInfoElement: HTMLElement;
  // If the reset variable is initialized with 0, the table does NOT get the items for the first time. Otherwise, it DOES.
  private _reset: number;
  private _reload: number;
  private _reloadInterval: number;
  private _lazy: boolean;
  private _model: any[];

  constructor(
    private http: HttpClient,
    private loaderService: LoaderService,
    private renderer: Renderer2,
    private translateService: TranslateService,
    private dispatcher: DispatcherService
  ) {
  }

  get model() {
    return this._model;
  }

  @Input()
  set model(val) {
    if (!val || !val.length) {
      this.setValue([]);
      this._model = val;
      this.pTreeTable.totalRecords = 0;
      this.lastFirst = 0;
      this.setPaginationInfo(this.pTreeTable.totalRecords, this.lastFirst, this.pTreeTable.rows);
    } else {
      this.itemAuxId = 0;
      this._model = val;
      this.setValue(this.parseToTreeNode(this.model, 1));
    }
  }

  get reset() {
    return this._reset;
  }

  @Input()
  set reset(val) {
    this._reset = val;
    if (val) {
      setTimeout(() => {
        this.resetSortFields();
        this.unselectAllItems();
        this.getItems();
        this.pTreeTable.first = 0;
      });
    }
  }

  get reload() {
    return this._reload;
  }

  @Input()
  set reload(val) {
    this._reload = val;
    if (val) {
      this.unselectAllItems();
      this.reloadTreeTable();
    }
  }

  get reloadInterval() {
    return this._reloadInterval;
  }

  @Input()
  set reloadInterval(val) {
    this._reloadInterval = val;
    if (val) {
      this.reloadTreeTableSubscription = setInterval(() => this.reloadTreeTable(true), val) as any;
    }
  }

  get lazy() {
    return this._lazy;
  }

  @Input()
  set lazy(val) {
    this._lazy = val;
    this.pTreeTable.lazy = val;
    if (!val) {
      setTimeout(() => {
        this.setPaginationInfo(this.pTreeTable.value.length, 0, this.pTreeTable.rows);
      });
    }
  }

  ngOnInit(): void {
    this.decorateTreeTable();
    this.getTranslations();
    this.initHttpRequestObservable();

    // If there are more than one table in the same page, they need an id to be differentiated.
    const observerKey = 'tableComponent' + this.id;
    this.dispatcher.register('idle-time-inactivity', () => this.onActivateDOM(false), observerKey);
    this.dispatcher.register('idle-time-activity', () => this.onActivateDOM(true), observerKey);
  }

  ngAfterContentInit() {
    this.setConfig();
    this.pTreeTable.onLazyLoad.subscribe((data) => {
      this.unselectAllItems();
      this.onLazyLoad(data);
    });
    this.pTreeTable.onPage.subscribe(data => this.onPage(data));
    this.pTreeTable.onNodeExpand.subscribe(event => this.onNodeExpand(event));
  }

  async getTranslations(): Promise<void> {
    this.rowsText = await this.translateService.get('CORE_ROWS').toPromise();
    this.ofText = await this.translateService.get('CORE_OF').toPromise();
  }

  onActivateDOM(activateFlag = true): void {
    this.activeDom = activateFlag;
  }

  initHttpRequestObservable(): void {
    if (!this.path) {
      return;
    }

    this.httpRequest$.pipe(
      switchMap(() => {
        const url = `${this.path}?${this.filters}`;
        return this.http.get(url, {params: this.lastHttpParams, observe: 'response'}).pipe(
          catchError(e => {
            return of(e);
          })
        );
      })
    ).subscribe(response => {
      if (response['error']) {
        this.processHttpErrorResponse(response);
      } else {
        this.processHttpResponse(response);
      }
    });
  }

  setConfig(): void {
    this.pTreeTable.paginator = this.pTreeTable.paginator !== undefined ? this.pTreeTable.paginator : treeTableDefaultConfig.paginator;
    this.pTreeTable.rows = this.pTreeTable.rows !== undefined ? this.pTreeTable.rows : treeTableDefaultConfig.rows;
    this.pTreeTable.rowsPerPageOptions = this.pTreeTable.rowsPerPageOptions !== undefined ? this.pTreeTable.rowsPerPageOptions : treeTableDefaultConfig.rowsPerPageOptions;
    this.pTreeTable.resizableColumns = this.pTreeTable.resizableColumns !== undefined ? this.pTreeTable.resizableColumns : treeTableDefaultConfig.resizableColumns;
    if (this.lazy === undefined) {
      this.pTreeTable.lazy = treeTableDefaultConfig.lazy;
      this.lazy = treeTableDefaultConfig.lazy;
    } else {
      this.pTreeTable.lazy = this.lazy;
    }
  }

  onLazyLoad(data): void {
    this.lastLazyLoadData = data;
    const page = (data.first / data.rows).toString();
    let sort;
    switch (data.sortOrder) {
      case 1:
        sort = `${data.sortField},asc`;
        break;
      case -1:
        sort = `${data.sortField},desc`;
        break;
      default:
    }

    this.getItems(page, null, sort, data.first, data.rows);
  }

  async onPage(data): Promise<void> {
    if (!this.lazy) {
      await this.setPaginationInfo(this.pTreeTable.value.length, data.first, data.rows);
    }
  }

  getItems(page = '0', size?: string, sort?: string, first = 0, rows?: number): void {
    if (!this.isLoading) {
      this.loaderService.start();
      this.isLoading = true;
    }

    let httpParams = new HttpParams();
    if (this.pTreeTable.paginator) {
      httpParams = httpParams
        .set('page', page)
        .set('size', size || this.pTreeTable.rows.toString());
    }

    if (sort) {
      httpParams = httpParams.set('sort', sort);
    }

    this.lastHttpParams = httpParams;
    this.lastFirst = first;
    this.lastRows = rows;

    this.getHttpRequest();
  }

  getHttpRequest(): void {
    this.httpRequest$.next();
  }

  async processHttpErrorResponse(response: HttpResponse<any>): Promise<void> {
    this.pTreeTable.value = [];
    this.pTreeTable.totalRecords = 0;
    await this.setPaginationInfo(this.pTreeTable.totalRecords, this.lastFirst, this.lastRows || this.pTreeTable.rows);
    this.loaderService.stop();
    this.isLoading = false;
  }

  async processHttpResponse(response: HttpResponse<any>): Promise<void> {
    this.model = response.body;
    this.pTreeTable.totalRecords = parseInt(response.headers.get('x-total-count'), 10);

    await this.setPaginationInfo(this.pTreeTable.totalRecords, this.lastFirst, this.lastRows || this.pTreeTable.rows);

    this.loaderService.stop();
    this.isLoading = false;
  }

  async setPaginationInfo(totalRows, originalFirst, rows): Promise<void> {
    if (!this.pTreeTable.paginator) {
      return;
    }

    const firstRow = originalFirst + 1;
    let lastRow = originalFirst + rows;
    lastRow = lastRow < totalRows ? lastRow : totalRows;

    if (!this.paginationInfoElement) {
      const pTreeTableElement = this.pTreeTable.el.nativeElement;
      const uiPaginatorElement = pTreeTableElement.querySelector('.p-paginator');
      const uiDropdownElement = uiPaginatorElement.querySelector('.p-dropdown');

      // Create rows text.
      const rowsTextElement = this.renderer.createElement('div');
      const paginationRowsTextElement = this.renderer.createText(this.rowsText);
      rowsTextElement.appendChild(paginationRowsTextElement);
      this.renderer.insertBefore(uiDropdownElement.parentElement, rowsTextElement, uiDropdownElement);

      // Create pagination info container.
      this.paginationInfoElement = this.renderer.createElement('div');
      this.renderer.appendChild(uiDropdownElement.parentElement, this.paginationInfoElement);
    } else {
      this.renderer.removeChild(this.paginationInfoElement, this.paginationInfoElement.firstChild);
    }

    const paginationInfoText = `${firstRow}-${lastRow} ${this.ofText} ${totalRows}`;
    const paginationInfoTextElement = this.renderer.createText(paginationInfoText);
    this.paginationInfoElement.appendChild(paginationInfoTextElement);
  }

  async getChildItems(node: SiTreeNode): Promise<void> {
    try {
      this.loaderService.start();
      let nodeAux: SiTreeNode = {...node};

      // Step 1. Add all the parent ids to an array.
      const levelIds = [nodeAux.data[this.valueAttrParent]];
      let level = 2;

      while (nodeAux.parent) {
        levelIds.push(nodeAux.parent.data[this.valueAttrParent]);
        nodeAux = nodeAux.parent;
        level++;
      }

      // Step 2. Parse each id to its respective level.
      let queryParams = levelIds.map((levelId, index) => `level${levelIds.length - index}=${levelId}`).join('&');

      if (this.valueType && node.data[this.valueType]) {
        queryParams += `&${this.valueType}=${node.data[this.valueType]}`;
      }

      const filtersParams = this.filters ? `&${this.filters}` : '';
      const url = `${this.path}?${queryParams}${filtersParams}`;
      const list = await this.http.get<any[]>(url).toPromise();

      node.children = this.parseToTreeNode(list, level, node);
      this.setValue(this.pTreeTable.value);
    } finally {
      this.loaderService.stop();
    }
  }

  parseToTreeNode(list: any[], level?: number, parentNode?: SiTreeNode): SiTreeNode[] {
    if (!list || !list.length) {
      return [];
    }

    const treeNode = [...list].map(node => {
      node.auxId = this.itemAuxId++;
      const auxNode = {
        data: node,
        leaf: !!node[this.valueAttr],
        parent: parentNode,
        level: level
      } as SiTreeNode;

      if (node.children) {
        auxNode.children = this.parseToTreeNode(node.children, undefined, auxNode);
      }

      return auxNode;
    });

    return treeNode;
  }

  setValue(newValue: any[]): void {
    const previousValue = [...this.pTreeTable.value];
    newValue = [...newValue];
    const simpleChange = new SimpleChange(previousValue, newValue, false);
    this.pTreeTable.value = newValue;

    // As we are setting the value of the tree table directly in value, the primeng component is not detecting changes,
    // so we have to force the execution of ngOnChanges as Angular would.
    this.pTreeTable.ngOnChanges({value: simpleChange});
  }

  unselectAllItems(): void {
    if (this.pTreeTable.selection) {
      this.pTreeTable.selection = null;
      this.pTreeTable.selectionChange.emit(null);
    }
  }

  reloadTreeTable(intervalReloading?: boolean): void {
    if (intervalReloading && this.activeDom) {
      return;
    }

    if (this.lastLazyLoadData) {
      this.onLazyLoad(this.lastLazyLoadData);
    } else {
      this.getItems();
    }
  }

  resetSortFields(): void {
    this.pTreeTable.sortField = null;
    this.pTreeTable.sortOrder = null;
  }

  async onNodeExpand(event: TreeTableNodeEvent): Promise<void> {
    if (event.node.children) {
      return;
    }

    await this.getChildItems(event.node);

    // Select children if the parent is already selected.
    if (this.pTreeTable.isSelected(event.node) && event.node.children && event.node.children.length) {
      for (let child of event.node.children) {
        const isDisabled = this.disabledRowFunction && this.disabledRowFunction(child.data);
        if (isDisabled) {
          this.pTreeTable.propagateSelectionDown(child, false);
          this.pTreeTable.propagateSelectionUp(child, false);
        } else {
          this.pTreeTable.propagateSelectionDown(child, true);
        }
      }

      this.pTreeTable.selectionChange.emit(this.pTreeTable.selection);
    }
  }

  deleteNode(itemAuxId: number, model: any[], treeTable: any[]): void {
    if (!model || !model.length) {
      return;
    }

    let index = 0;
    const listLength = model.length;
    while (!this.nodeToBeDeletedFound && index < listLength) {
      if (itemAuxId === model[index].auxId) {
        model.splice(index, 1);
        treeTable.splice(index, 1);
        this.nodeToBeDeletedFound = true;
        break;
      }

      if (model[index].children) {
        this.deleteNode(itemAuxId, model[index].children, treeTable[index].children);
      }

      if (model[index].children && model[index].children.length === 0) {
        model.splice(index, 1);
        treeTable.splice(index, 1);
      }

      index++;
    }
  }

  onClickDeleteNode(itemAuxId: number): void {
    if (itemAuxId === undefined || itemAuxId === null) {
      return;
    }

    this.deleteNode(itemAuxId, this.model, this.pTreeTable.value);
    this.nodeToBeDeletedFound = false;
    this.setValue(this.pTreeTable.value);
    this.modelChange.emit(this.model);
  }

  // Functions overwriting the real ones in the Tree Table component.
  async decorateTreeTable(): Promise<void> {
    if (!this.pTreeTable) {
      return;
    }

    // PropagateSelectionDown overwritten to not check disabled checkboxes.
    const originalPropagateSelectionDown = this.pTreeTable.propagateSelectionDown.bind(this.pTreeTable);
    this.pTreeTable.propagateSelectionDown = (node: SiTreeNode, select: boolean) => {
      if (!this.disabledRowFunction || !this.disabledRowFunction(node.data)) {
        originalPropagateSelectionDown(node, select);
      } else {
        originalPropagateSelectionDown(node, false);
      }
    };
  }

  ngOnDestroy() {
    const observerKey = 'tableComponent' + this.id;
    this.dispatcher.unregisterFunction('idle-time-inactivity', observerKey);
    this.dispatcher.unregisterFunction('idle-time-activity', observerKey);
    if (this.reloadTreeTableSubscription) {
      clearInterval(this.reloadTreeTableSubscription);
    }

    this.httpRequest$.complete();
  }
}
